import type { FC } from "react";
import { useState } from "react";
import Box from "@mui/material/Box";
import { Translate, useTranslation } from "@privacy/translations";
import { DynamicForm } from "../../../dynamicForm";
import {
  useDeletePersonalDataController,
  useGetPersonalDataControllerById,
  useGetPersonalDataControllerForm,
  useUpdatePersonalDataControllerAnswers,
} from "../../../api/personalDataControllers";
import type { PersonalDataControllerById } from "../../../api/personalDataControllers.types";
import { TenantIcon2 } from "../../../shared/icons";
import type { DrawerHeaderProps } from "../../../shared/Drawer";
import { Drawer } from "../../../shared/Drawer";
import { DynamicGrid } from "../../../dynamicGrid";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { ActionMenu, ActionMenuDeleteMenuItem } from "../../../shared/ActionMenu";
import type { TranslationKey } from "@privacy/translations";

export const PersonalDataControllersInlineFormDrawer = () => {
  const personalDataControllerId = DynamicGrid.useSelectedRowId();
  const personalDataControllerQuery = useGetPersonalDataControllerById(personalDataControllerId);
  const personalDataControllerFormQuery = useGetPersonalDataControllerForm();
  const { mutateAsync, isError: isSaveError, isPending } = useUpdatePersonalDataControllerAnswers();

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!personalDataControllerQuery.data || !personalDataControllerFormQuery.data) return <Drawer.Loading />;

        const personalDataController = personalDataControllerQuery.data;
        return (
          <DynamicForm.Provider
            onSave={answer => mutateAsync({ personalDataControllerId, answers: [answer] })}
            isReadOnly={false}
            isSaving={isPending}
            isSaveError={isSaveError}
            key={personalDataControllerId}
            answers={personalDataController.answers}
          >
            {({ formState }) => (
              <>
                <PersonalDataControllerHeader
                  personalDataController={personalDataController}
                  statusIndicator={DynamicForm.formStateToDrawerHeaderStatus(formState)}
                />
                <Drawer.Content>
                  <DynamicForm.InlineForm id={personalDataController.id} form={personalDataControllerFormQuery.data} />
                </Drawer.Content>
              </>
            )}
          </DynamicForm.Provider>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

type PersonalDataControllersDrawerHeaderProps = {
  personalDataController: PersonalDataControllerById;
  statusIndicator: DrawerHeaderProps["status"];
};

const PersonalDataControllerHeader: FC<PersonalDataControllersDrawerHeaderProps> = ({
  personalDataController,
  statusIndicator,
}) => {
  const { navigate, paths } = usePrivacyNavigate();

  return (
    <Drawer.Header
      onClose={() => navigate(paths.pdcGrid())}
      status={statusIndicator}
      title={personalDataController.name}
      renderIcon={props => <TenantIcon2 {...props} />}
      topRightSlot={
        <PersonalDataControllerActionMenu
          personalDataControllerId={personalDataController.id}
          personalDataControllerName={personalDataController.name}
          onDeleted={() => navigate(paths.pdcGrid())}
          ariaLabelKey="GridDrawer.Menu.ButtonLabel"
        />
      }
    />
  );
};

type PersonalDataControllerActionMenuProps = {
  personalDataControllerId: string;
  personalDataControllerName: string;
  onDeleted: () => void;
  ariaLabelKey?: TranslationKey;
};
export const PersonalDataControllerActionMenu = ({
  personalDataControllerId,
  personalDataControllerName,
  onDeleted,
  ariaLabelKey,
}: PersonalDataControllerActionMenuProps) => {
  const { t } = useTranslation();
  const deletePersonalDataController = useDeletePersonalDataController();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ActionMenu isOpen={isOpen} onOpenChange={setIsOpen} ariaLabelKey={ariaLabelKey}>
      <ActionMenuDeleteMenuItem
        confirmDeleteDialog={{
          header: t("PersonalDataControllersDrawer.Delete.ConfirmHeader"),
          message: (
            <Translate
              translationKey="PersonalDataControllersDrawer.Delete.ConfirmMessage"
              values={{
                name: (
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {personalDataControllerName}
                  </Box>
                ),
              }}
            />
          ),
          delete: t("PersonalDataControllersDrawer.Delete.ConfirmDelete"),
        }}
        onClose={() => setIsOpen(false)}
        onDelete={() =>
          deletePersonalDataController.mutateAsync(personalDataControllerId, { onSuccess: () => onDeleted() })
        }
      />
    </ActionMenu>
  );
};
